import {
  Badge,
  Box,
  HStack,
  Icon,
  Link,
  SimpleGrid,
  Skeleton,
  Spacer,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react';
import {
  CartTotalBody,
  CartTotalWithMagnify,
  DetailProduct,
} from '@diamond/shared/types';
import {
  Button,
  Carousel,
  IconButton,
  LabelPromoList,
  PromoPriceIntegration as PromoPrice,
  Text,
} from '@diamond/shared/ui';
import {
  buyerCurrencyFormatter,
  getItemCardTrackingParams,
  storageFormatFormatter,
  trackEvent,
} from '@diamond/shared/utils';
import {
  DEFAULT_OUTLET_NAME_FALLBACK,
  generateFnBEquipmentWhatsAppUrl,
  TRACKING_EVENTS,
  TRACKING_PAGES,
} from '@diamond/sol-buyer/constant';
import {
  mergeProcessPromoWithCartTotal,
  processPromos,
  processPromoValidation as processPromoCartTotal,
} from '@diamond/sol-buyer/utils';
import { useCartStore } from '@diamond/sol-buyer-context';
import { Favorite, FavoriteBorder, WhatsApp } from '@mui/icons-material';
import { type MouseEvent, useMemo } from 'react';

import { VoucherCountBadge } from '../../voucher/VoucherCountBadge';
import { PromoInfoListProductDetail } from './PromoInfoListProductDetail';
import { RecipeCount } from './RecipeCount';

interface CardProductDetailProps {
  data: DetailProduct;
  unitPrice: number;
  addToWishList: () => void;
  hasActiveDiscount?: boolean;
  product: CartTotalBody['products'][number];
  section?: string;
  cartTotalData?: CartTotalWithMagnify;
  isCartTotalFetching?: boolean;
  /** Feature-flag gate for voucher display refinement */
  isVoucherDisplayEnabled?: boolean;
  /** Optional click override for voucher badge CTA */
  onVoucherBadgeClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  /** Optional destination for voucher badge */
  voucherBadgeHref?: string;
  /** When true, shows "Tanya produk" button for F&B Equipment */
  isFnBEquipment?: boolean;
  /** Outlet name for WhatsApp message prefill */
  outletName?: string;
}

export function CardProductDetail({
  data,
  unitPrice,
  addToWishList,
  hasActiveDiscount,
  product,
  section = 'undefined',
  cartTotalData,
  isCartTotalFetching,
  isVoucherDisplayEnabled = true,
  onVoucherBadgeClick,
  voucherBadgeHref = '/voucher',
  isFnBEquipment = false,
  outletName,
}: CardProductDetailProps) {
  const arrImages =
    data.product_images_large && data.product_images_large.length > 0
      ? data.product_images_large.map((val) => ({
          image: val,
          url: '',
        }))
      : [{ image: '/images/product_placeholder.png', url: '' }];

  const processedPromo = processPromos(data);

  const {
    hasDiscount,
    hasBundledDiscount,
    priceAfterDiscount,
    hasFreeGoods,
    hasBundledFreeGoods,
  } = processedPromo;

  const promoRecord = useCartStore(
    (state) => state.promoRecord,
    (a, b) => JSON.stringify(a) === JSON.stringify(b)
  );

  const processedPromoCartTotal = useMemo(
    () => processPromoCartTotal(cartTotalData?.promos?.[0]),
    [cartTotalData?.promos]
  );

  const mergedProcessedPromo = useMemo(
    () =>
      mergeProcessPromoWithCartTotal(processedPromo, processedPromoCartTotal),
    [processedPromo, processedPromoCartTotal]
  );

  const compiledPromo = promoRecord[data.id];
  const compiledPromoPrice = compiledPromo?.price_after_discount || 0;
  const hasCompiledDiscount = compiledPromoPrice > 0;

  const hasFreeGoodsOnly = hasFreeGoods || hasBundledFreeGoods;
  const isPriceZero = unitPrice === 0;
  const selectedQuantity =
    product?.quantity ?? data?.minimum_order_quantity ?? 0;
  const voucherCount = Number(data?.count_voucher ?? 0);

  const shouldRenderVoucherBadge =
    isVoucherDisplayEnabled && selectedQuantity > 0 && voucherCount > 0;

  const handleVoucherBadgeClick = (event: MouseEvent<HTMLAnchorElement>) => {
    const trackingQuantity = selectedQuantity / 1000;

    trackEvent(TRACKING_EVENTS.PRE_ADD_TO_CART, {
      ...getItemCardTrackingParams(
        data,
        trackingQuantity,
        TRACKING_PAGES.PRODUCT_DETAIL,
        TRACKING_PAGES.PRODUCT_DETAIL
      ),
      section_name: TRACKING_PAGES.PRODUCT_DETAIL,
      source: 'detail-voucher-badge',
    });

    onVoucherBadgeClick?.(event);
  };

  return (
    <>
      <Box
        w={{ base: 'full', md: '320px' }}
        id="product-detail-image"
        display="flex"
        flexDir={{ base: 'column-reverse', md: 'column' }}
        gap="4"
      >
        <Carousel
          duration={8000}
          imageData={arrImages}
          h="320px"
          isProductDetail
        />
        <RecipeCount
          count={data?.count_recipes}
          name={data?.desc}
          productId={data?.id}
          itemCode={data?.item_code?.toString()}
          brandName={data?.brand_name}
        />
      </Box>
      <Box w={{ base: 'full', md: '50%' }} px={{ base: 2, md: 4 }} pt={4}>
        <HStack>
          <Stack w="full">
            <Box
              display={{ base: 'flex', md: 'none' }}
              px={{ base: 0, md: 4 }}
              gap="2"
              minH="20px"
              flexWrap="wrap"
              alignItems="center"
            >
              <LabelPromoList processedPromo={mergedProcessedPromo} />
            </Box>
            <HStack>
              <Text
                id="brand-name"
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="bold"
              >
                {data?.brand_desc}
              </Text>
              {isPriceZero && (
                <Badge
                  fontSize="xs"
                  fontWeight="semibold"
                  px="2"
                  py="1"
                  borderRadius="md"
                  colorScheme="gray"
                >
                  Hubungi Sales
                </Badge>
              )}
              <Spacer />
              <Box
                display={{ base: 'none', md: 'flex' }}
                gap="2"
                alignItems="center"
              >
                <LabelPromoList processedPromo={mergedProcessedPromo} />
              </Box>
              {data?.wishlist_id ? (
                <IconButton
                  aria-label="wishlist"
                  name="wishlist"
                  icon={<Favorite />}
                  size="sm"
                  color="red"
                  _hover={{ color: 'red' }}
                  display={{ base: 'flex', md: 'none' }}
                  onClick={addToWishList}
                />
              ) : (
                <IconButton
                  aria-label="unwishlist"
                  name="unwishlist"
                  icon={<FavoriteBorder />}
                  size="sm"
                  color="gray.75"
                  _hover={{ color: 'red' }}
                  display={{ base: 'flex', md: 'none' }}
                  onClick={addToWishList}
                />
              )}
            </HStack>
            <Text fontSize={{ base: 'sm', md: 'md' }} mt="5px !important">
              {data?.desc}
            </Text>
            <Box py="2">
              {isCartTotalFetching && !hasFreeGoodsOnly ? (
                <Skeleton h="20px" w="150px" />
              ) : hasDiscount || hasBundledDiscount ? (
                <PromoPrice
                  data={data}
                  promoDeductionAmount={
                    hasCompiledDiscount
                      ? compiledPromo?.deduction_amount
                      : hasBundledDiscount
                      ? data.bundled_discount_promos[0].deduction_amount
                      : data.discount_promos[0].deduction_amount
                  }
                  promoIsDiscount={
                    hasCompiledDiscount
                      ? compiledPromo?.is_discount
                      : hasBundledDiscount
                      ? data.bundled_discount_promos[0].is_discount
                      : data.discount_promos[0].is_discount
                  }
                  isBundled={hasBundledDiscount && !hasDiscount}
                  hasActiveDiscount={hasActiveDiscount || hasCompiledDiscount}
                />
              ) : null}
              {isCartTotalFetching && !hasFreeGoodsOnly ? (
                <Skeleton mt="1" h="40px" w="250px" />
              ) : (
                <Text
                  as={'span'}
                  fontWeight="bold"
                  fontSize={{ base: 'lg', md: '3xl' }}
                >
                  {isPriceZero
                    ? '-'
                    : buyerCurrencyFormatter(
                        hasCompiledDiscount
                          ? compiledPromoPrice
                          : hasDiscount && hasActiveDiscount
                          ? priceAfterDiscount
                          : unitPrice
                      )}
                </Text>
              )}
              {!isPriceZero ? (
                <Text
                  as={'span'}
                  color="gray.75"
                  fontSize={{ base: '12px', md: '16px' }}
                >
                  / {data?.sales_unit}
                </Text>
              ) : null}
              {data?.pack_unit && (
                <Text color="gray.75" fontSize={{ base: 'md' }}>
                  Pak: {data?.pack_unit}
                </Text>
              )}
              <Text color="gray.75" fontSize={{ base: 'sm' }}>
                {data?.item_code}
              </Text>
              {shouldRenderVoucherBadge ? (
                <Box mt="2">
                  <VoucherCountBadge
                    itemCode={data?.item_code}
                    count={data?.count_voucher}
                    href={voucherBadgeHref}
                    onClick={handleVoucherBadgeClick}
                    aria-label="lihat-voucher-produk"
                  />
                </Box>
              ) : null}
            </Box>
            <PromoInfoListProductDetail
              processedPromo={mergedProcessedPromo}
              isFetching={isCartTotalFetching}
              section={section}
            />
          </Stack>
        </HStack>
        <Tabs colorScheme="blue" height={{ base: 'sm', md: 'auto' }}>
          <Box
            flex="1"
            w="full"
            overflowX="auto"
            __css={{
              scrollbarWidth: 'none',
              '::-webkit-scrollbar': { display: 'none' },
              boxShadow: 'inset 0 -2px 0 rgba(0, 0, 0, 0.1)',
              border: '0 none',
            }}
          >
            <TabList borderColor="gray.33">
              <Tab
                _selected={{ borderColor: 'blue', color: 'blue' }}
                color="gray.33"
              >
                Informasi
              </Tab>
              <Tab
                _selected={{ borderColor: 'blue', color: 'blue' }}
                color="gray.33"
              >
                Komposisi
              </Tab>
              <Tab
                _selected={{ borderColor: 'blue', color: 'blue' }}
                color="gray.33"
              >
                Keunggulan
              </Tab>
              <Tab
                _selected={{ borderColor: 'blue', color: 'blue' }}
                color="gray.33"
              >
                Deskripsi
              </Tab>
            </TabList>
          </Box>
          <TabPanels color="black">
            <TabPanel>
              <Text fontSize={{ base: 'xs', md: 'sm' }}>
                {data?.information}
              </Text>
            </TabPanel>
            <TabPanel>
              <Text fontSize={{ base: 'xs', md: 'sm' }}>
                {data?.composition}
              </Text>
            </TabPanel>
            <TabPanel>
              <Text fontSize={{ base: 'xs', md: 'sm' }}>
                {data?.superiority}
              </Text>
            </TabPanel>
            <TabPanel>
              <SimpleGrid
                columns={2}
                spacing={2}
                shadow={{ base: 'base', md: 'none' }}
                rounded={{ base: 'lg', md: 'none' }}
                border={{ base: '1px', md: 'none' }}
                borderColor={{ base: 'gray.33', md: 'unset' }}
              >
                <Box
                  shadow={{ base: 'none', md: 'base' }}
                  rounded={{ base: 'none', md: 'lg' }}
                  px="4"
                  border={{ base: 'unset', md: '1px' }}
                  borderColor={{ base: 'unset', md: 'gray.33' }}
                >
                  <Stack>
                    <Box>
                      <Text color="blue">Kode Item</Text>
                      <Text fontWeight="semibold">{data?.item_code}</Text>
                    </Box>
                    <Box>
                      <Text color="blue">Brand</Text>
                      <Text fontWeight="semibold">{data?.brand_desc}</Text>
                    </Box>
                    <Box>
                      <Text color="blue">Tipe</Text>
                      <Text fontWeight="semibold">{data?.type}</Text>
                    </Box>
                    <Box>
                      <Text color="blue">Tempat Penyimpanan</Text>
                      <Text fontWeight="semibold">
                        {storageFormatFormatter(data?.product_storage)}
                      </Text>
                    </Box>
                    <Box>
                      <Text color="blue">Dimensi</Text>
                      <Text fontWeight="semibold">{data?.dimension}</Text>
                    </Box>
                  </Stack>
                </Box>
                <Box
                  shadow={{ base: 'none', md: 'base' }}
                  rounded={{ base: 'none', md: 'lg' }}
                  py="2"
                  px="4"
                  border={{ base: 'unset', md: '1px' }}
                  borderColor={{ base: 'unset', md: 'gray.33' }}
                >
                  <Stack>
                    <Box>
                      <Text color="blue">Kategori Utama</Text>
                      <Text fontWeight="semibold">{data?.category_name}</Text>
                    </Box>
                    <Box>
                      <Text color="blue">Sub Kategori</Text>
                      <Text fontWeight="semibold">
                        {data?.sub_category_name}
                      </Text>
                    </Box>
                    <Box>
                      <Text color="blue">Satuan Pengukuran</Text>
                      <Text fontWeight="semibold">{data?.sales_unit}</Text>
                    </Box>
                    <Box>
                      <Text color="blue">Satuan Perpak</Text>
                      <Text fontWeight="semibold">{data?.pack_unit}</Text>
                    </Box>
                  </Stack>
                </Box>
              </SimpleGrid>
            </TabPanel>
          </TabPanels>
        </Tabs>
        {isFnBEquipment && (
          <Box mt="4">
            <Link
              href={generateFnBEquipmentWhatsAppUrl(
                String(data?.item_code) || '',
                data?.brand_desc || '',
                data?.desc || '',
                outletName || DEFAULT_OUTLET_NAME_FALLBACK
              )}
              isExternal
              _hover={{ textDecoration: 'none' }}
              onClick={() => {
                // GA4 e-commerce compliant tracking with items[] array
                const itemParams = getItemCardTrackingParams(
                  data,
                  1,
                  TRACKING_PAGES.PRODUCT_DETAIL,
                  TRACKING_PAGES.PRODUCT_DETAIL
                );
                trackEvent(TRACKING_EVENTS.GENERATE_LEAD, {
                  currency: 'IDR',
                  value: itemParams.price || 0,
                  lead_source: 'whatsapp',
                  items: [itemParams],
                });
              }}
            >
              <Button
                variant="outline"
                borderColor="#0000A3"
                color="#0000A3"
                leftIcon={<Icon as={WhatsApp} />}
                _hover={{
                  bg: '#E8E8FF',
                  borderColor: '#000080',
                  color: '#000080',
                }}
              >
                Tanya produk
              </Button>
            </Link>
          </Box>
        )}
      </Box>
    </>
  );
}

export default CardProductDetail;
